import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users, Calendar, TrendingUp, Eye, FileText, 
  ChevronRight, BarChart3, GraduationCap, Download
} from "lucide-react";
import axios from "axios";
import ProficiencySpiderChart from "@/components/ProficiencySpiderChart";
import MultipleMeasuresDisclaimer from "@/components/MultipleMeasuresDisclaimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Results = () => {
  const [results, setResults] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [gradesRes, resultsRes] = await Promise.all([
          axios.get(`${API}/grades`),
          axios.get(`${API}/results`)
        ]);
        setGrades(gradesRes.data.grades);
        setResults(resultsRes.data.results);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredResults = selectedGrade === "all" 
    ? results 
    : results.filter(r => r.grade_level === selectedGrade);

  const getProficiencyBadgeClass = (level) => {
    return `wida-badge level-${level}`;
  };

  const gradeLabels = {
    "K": "Kindergarten",
    "1": "Grade 1",
    "2": "Grade 2",
    "3-4": "Grades 3-4",
    "5-6": "Grades 5-6",
    "7-8": "Grades 7-8",
    "9-12": "Grades 9-12"
  };

  // Calculate statistics
  const stats = {
    total: filteredResults.length,
    avgScore: filteredResults.length > 0 
      ? (filteredResults.reduce((acc, r) => acc + r.overall_score, 0) / filteredResults.length).toFixed(1)
      : 0,
    avgLevel: filteredResults.length > 0
      ? Math.round(filteredResults.reduce((acc, r) => acc + r.wida_level, 0) / filteredResults.length)
      : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="results-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-[#002FA7]">Home</Link>
        <span>/</span>
        <span className="text-gray-900">Results</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Assessment Results
          </h1>
          <p className="text-gray-600">
            View and analyze student language-proficiency scores
          </p>
          <MultipleMeasuresDisclaimer variant="compact" className="mt-3 max-w-2xl" />
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/my-feedback">
            <Button variant="outline" className="btn-sharp h-10" data-testid="my-feedback-link">
              <FileText size={16} className="mr-2" /> My Feedback
            </Button>
          </Link>
          <Link to="/my-progress">
            <Button variant="outline" className="btn-sharp h-10" data-testid="my-progress-link">
              <TrendingUp size={16} className="mr-2" /> My Progress
            </Button>
          </Link>
          <Button
            variant="outline"
            className="btn-sharp h-10"
            onClick={() => {
              const gradeParam = selectedGrade !== 'all' ? `?grade_level=${selectedGrade}` : '';
              window.open(`${API}/export/results${gradeParam}`, '_blank');
            }}
            data-testid="export-csv-btn"
          >
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
          <Select value={selectedGrade} onValueChange={setSelectedGrade}>
            <SelectTrigger className="w-48 h-12 border-2 border-gray-300 rounded-none" data-testid="filter-grade">
              <SelectValue placeholder="Filter by Grade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Grades</SelectItem>
              {grades.map(grade => (
                <SelectItem key={grade.value} value={grade.value}>
                  {grade.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-gray-300 mb-8">
        <div className="swiss-card cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => document.getElementById('results-table')?.scrollIntoView({ behavior: 'smooth' })} data-testid="stat-total-assessments">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 text-blue-700 flex items-center justify-center">
              <Users size={20} />
            </div>
            <span className="text-sm text-gray-500">Total Assessments</span>
          </div>
          <div className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats.total}
          </div>
        </div>
        <div className="swiss-card cursor-pointer hover:bg-green-50 transition-colors" onClick={() => document.getElementById('results-table')?.scrollIntoView({ behavior: 'smooth' })} data-testid="stat-avg-score">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-green-100 text-green-700 flex items-center justify-center">
              <TrendingUp size={20} />
            </div>
            <span className="text-sm text-gray-500">Avg Proficiency Score</span>
          </div>
          <div className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats.avgScore}
          </div>
        </div>
        <div className="swiss-card cursor-pointer hover:bg-purple-50 transition-colors" onClick={() => document.getElementById('results-table')?.scrollIntoView({ behavior: 'smooth' })} data-testid="stat-avg-level">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-100 text-purple-700 flex items-center justify-center">
              <BarChart3 size={20} />
            </div>
            <span className="text-sm text-gray-500">Avg Proficiency Level</span>
          </div>
          <div className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {stats.avgLevel}
          </div>
        </div>
      </div>

      {/* Student Spider Chart */}
      {filteredResults.length > 0 && (() => {
        // Aggregate per-student modality averages
        const studentMap = {};
        filteredResults.forEach(r => {
          const id = r.student_id || r.student_name;
          if (!studentMap[id]) {
            studentMap[id] = { name: r.student_name, count: 0, listening: 0, speaking: 0, reading: 0, writing: 0 };
          }
          studentMap[id].count++;
          studentMap[id].listening += r.listening_wida || 0;
          studentMap[id].speaking += r.speaking_wida || 0;
          studentMap[id].reading += r.reading_wida || 0;
          studentMap[id].writing += r.writing_wida || 0;
        });
        const studentSeries = Object.values(studentMap)
          .map(s => ({
            name: s.name,
            listening: Math.round((s.listening / s.count) * 10) / 10,
            speaking: Math.round((s.speaking / s.count) * 10) / 10,
            reading: Math.round((s.reading / s.count) * 10) / 10,
            writing: Math.round((s.writing / s.count) * 10) / 10,
          }))
          .filter(s => s.listening || s.speaking || s.reading || s.writing)
          .slice(0, 10); // Max 10 students to keep chart readable

        // Overall average for overlay
        const totalStudents = studentSeries.length;
        const overallAvg = totalStudents > 0 ? {
          name: 'Class Average',
          listening: Math.round(studentSeries.reduce((a, s) => a + s.listening, 0) / totalStudents * 10) / 10,
          speaking: Math.round(studentSeries.reduce((a, s) => a + s.speaking, 0) / totalStudents * 10) / 10,
          reading: Math.round(studentSeries.reduce((a, s) => a + s.reading, 0) / totalStudents * 10) / 10,
          writing: Math.round(studentSeries.reduce((a, s) => a + s.writing, 0) / totalStudents * 10) / 10,
        } : null;

        if (!studentSeries.length) return null;

        return (
          <div className="mb-8" data-testid="results-spider-chart">
            <ProficiencySpiderChart
              series={studentSeries}
              overlay={totalStudents > 1 ? overallAvg : null}
              title={`Student Modality Performance${selectedGrade !== 'all' ? ` — ${gradeLabels[selectedGrade] || selectedGrade}` : ''}`}
              subtitle={`${totalStudents} student${totalStudents !== 1 ? 's' : ''} — proficiency scores across Listening, Speaking, Reading, Writing`}
              height={340}
            />
          </div>
        );
      })()}

      {/* Results Table */}
      {filteredResults.length === 0 ? (
        <div id="results-table" className="empty-state border-2 border-dashed border-gray-300">
          <FileText className="empty-state-icon" />
          <h3 className="empty-state-title">No Results Yet</h3>
          <p className="empty-state-description mb-6">
            Complete assessments will appear here. Start by selecting a grade level and taking an exam.
          </p>
          <Link to="/grades">
            <Button className="btn-sharp-primary">
              Start Assessment
              <ChevronRight size={18} className="ml-1" />
            </Button>
          </Link>
        </div>
      ) : (
        <div id="results-table" className="border-2 border-gray-200">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200 font-semibold text-sm">
            <div className="col-span-3">Student</div>
            <div className="col-span-2">Grade</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2 text-center">Score</div>
            <div className="col-span-2 text-center">Proficiency Level</div>
            <div className="col-span-1"></div>
          </div>
          
          {/* Table Rows */}
          {filteredResults.map((result, index) => (
            <div 
              key={result.id}
              className={`grid grid-cols-12 gap-4 p-4 items-center border-b border-gray-200 hover:bg-gray-50 transition-colors
                ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
              data-testid={`result-row-${result.id}`}
            >
              <div className="col-span-3">
                <div className="font-semibold">{result.student_name}</div>
                <div className="text-xs text-gray-500">ID: {result.exam_session_id.slice(0, 8)}...</div>
              </div>
              <div className="col-span-2">
                <div className="flex items-center gap-2">
                  <GraduationCap size={16} className="text-gray-400" />
                  {gradeLabels[result.grade_level] || result.grade_level}
                </div>
              </div>
              <div className="col-span-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar size={14} />
                  {new Date(result.scored_at).toLocaleDateString()}
                </div>
              </div>
              <div className="col-span-2 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-gray-300 font-bold text-xl">
                  {result.overall_score}
                </div>
              </div>
              <div className="col-span-2 text-center">
                <div className={getProficiencyBadgeClass(result.wida_level)}>
                  <span className="font-bold">{result.wida_level}</span>
                  <span className="text-sm">{result.wida_level_name}</span>
                </div>
              </div>
              <div className="col-span-1 text-right">
                <Link to={`/results/${result.exam_session_id}`}>
                  <Button variant="ghost" size="sm" className="p-2" data-testid={`view-result-${result.id}`}>
                    <Eye size={18} />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Results;
